import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { 
  Plus, Trash2, Play, Pause, Download, ChevronLeft, ChevronRight, 
  Search, Image, Copy, X, Edit2, Check, Loader2, Sparkles, FolderOpen, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useLanguage } from '../contexts/LanguageContext';
import { getProxyImageUrl, normalizeCard } from '../utils';
import { useSavedTheaters } from '../hooks/useSavedTheaters';
import SavedTheatersDialog from '../components/Dialogs/SavedTheatersDialog';
import html2canvas from 'html2canvas';
import GIF from 'gif.js';

// Card back image for face-down cards
const CARD_BACK_IMAGE = '/images/card-back.png';

// Maximum cards in a stack (quad = 4)
const MAX_STACK_SIZE = 4;

// Pixels of underlying card visible in a stack
const STACK_PEEK = 25;

// Zone types for card placement
const ZONES = {
  OFF_SCREEN: 'offScreen',
  OPPONENT_DECK: 'opponentDeck',
  OPPONENT_DISCARD: 'opponentDiscard',
  OPPONENT_HAND: 'opponentHand',
  OPPONENT_FIELD: 'opponentField',
  SCENE: 'scene',
  PLAYER_FIELD: 'playerField',
  PLAYER_HAND: 'playerHand',
  PLAYER_DECK: 'playerDeck',
  PLAYER_DISCARD: 'playerDiscard',
};

// Zone display config
const ZONE_CONFIG = {
  [ZONES.OFF_SCREEN]: { label: 'Standby', color: 'bg-slate-950/80', faceDown: false },
  [ZONES.OPPONENT_DECK]: { label: 'Deck', color: 'bg-red-900/30', faceDown: true },
  [ZONES.OPPONENT_DISCARD]: { label: 'Discard', color: 'bg-red-900/30', faceDown: false },
  [ZONES.OPPONENT_HAND]: { label: 'Opponent Hand', color: 'bg-red-900/20', faceDown: false },
  [ZONES.OPPONENT_FIELD]: { label: 'Opponent Field', color: 'bg-red-900/40', faceDown: false },
  [ZONES.SCENE]: { label: 'Scene Area', color: 'bg-amber-900/40', faceDown: false },
  [ZONES.PLAYER_FIELD]: { label: 'Player Field', color: 'bg-blue-900/40', faceDown: false },
  [ZONES.PLAYER_HAND]: { label: 'Hand', color: 'bg-blue-900/20', faceDown: false },
  [ZONES.PLAYER_DECK]: { label: 'Deck', color: 'bg-blue-900/30', faceDown: true },
  [ZONES.PLAYER_DISCARD]: { label: 'Discard', color: 'bg-blue-900/30', faceDown: false },
};

// Card glow effects
const CARD_EFFECTS = {
  'glow-blue': {
    label: 'Blue Glow',
    color: '#3b82f6',
    gradient: 'from-blue-400 via-blue-500 to-cyan-400',
    shadow: '0 0 12px 3px rgba(59,130,246,0.6), 0 0 24px 6px rgba(59,130,246,0.3)',
    border: 'border-blue-400/80',
  },
  'glow-red': {
    label: 'Red Glow',
    color: '#ef4444',
    gradient: 'from-red-400 via-red-500 to-orange-400',
    shadow: '0 0 12px 3px rgba(239,68,68,0.6), 0 0 24px 6px rgba(239,68,68,0.3)',
    border: 'border-red-400/80',
  },
  'glow-yellow': {
    label: 'Yellow Glow',
    color: '#eab308',
    gradient: 'from-yellow-300 via-yellow-400 to-amber-400',
    shadow: '0 0 12px 3px rgba(234,179,8,0.6), 0 0 24px 6px rgba(234,179,8,0.3)',
    border: 'border-yellow-400/80',
  },
  'remove': {
    label: 'Remove Effect',
    color: null,
    isRemove: true,
  },
};

// Default empty slide
const createEmptySlide = (id = Date.now()) => ({
  id,
  caption: '',
  zones: Object.keys(ZONES).reduce((acc, key) => {
    acc[ZONES[key]] = [];
    return acc;
  }, {}),
  // Track stacked cards - key is position index, value is array of instanceIds
  stacks: {},
});

// Card instance with unique ID for tracking across slides
const createCardInstance = (card, instanceId = `${Date.now()}-${Math.random()}`) => ({
  ...card,
  instanceId,
  // Track if card is entering/leaving off-screen for fade animation
  fadeState: null, // 'entering' | 'leaving' | null
});

// Get card image URL - handles various image structures
const getCardImageUrl = (card, size = 'thumbnail') => {
  if (!card || !card.images) return '';
  
  const images = card.images;
  
  // Try blob images first (preferred)
  if (images.blob) {
    if (size === 'full' && images.blob.full) return images.blob.full;
    if (images.blob.thumbnail) return images.blob.thumbnail;
    if (images.blob.full) return images.blob.full;
  }
  
  // Fallback to original images
  if (images.original) {
    if (size === 'full' && images.original.full) return images.original.full;
    if (images.original.thumbnail) return images.original.thumbnail;
    if (images.original.full) return images.original.full;
  }
  
  // Handle language-specific images (en, jp, etc.)
  const langKeys = ['en', 'EN', 'jp', 'JP'];
  for (const lang of langKeys) {
    if (images[lang]) {
      if (images[lang].small) return images[lang].small;
      if (images[lang].large) return images[lang].large;
    }
  }
  
  // Last resort - try small/large directly
  if (images.small) return images.small;
  if (images.large) return images.large;
  
  return '';
};

/**
 * Theater Mode - Create card game state animations
 * Layout mirrors the actual game board with opponent/player fields
 */
export default function Animations() {
  const { t, language } = useLanguage();
  
  // Save/load theaters
  const {
    savedTheaters, saveTheater, deleteTheater, renameTheater,
    saveSessionTheater, loadSessionTheater,
    canSaveMore, maxTheaters,
  } = useSavedTheaters();
  const [showSavedTheaters, setShowSavedTheaters] = useState(false);
  const sessionRestoredRef = useRef(false);
  
  // Slides state
  const [slides, setSlides] = useState([createEmptySlide()]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed] = useState(1500);
  
  // Card search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [targetZone, setTargetZone] = useState(null);
  
  // Caption editing
  const [editingCaption, setEditingCaption] = useState(false);
  const [captionText, setCaptionText] = useState('');
  
  // Drag and drop state - refs hold the authoritative data, state is for visual feedback only
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragSourceZone, setDragSourceZone] = useState(null);
  const [dropTargetZone, setDropTargetZone] = useState(null);
  const [_dragSourceIndex, setDragSourceIndex] = useState(null); // eslint-disable-line no-unused-vars
  const [dropTargetIndex, setDropTargetIndex] = useState(null); // For horizontal reordering in fields
  // Refs for drag data - avoids re-render during dragstart that kills the drag
  const dragDataRef = useRef({ card: null, zone: null, index: null });
  // Effect drag state
  const [draggingEffect, setDraggingEffect] = useState(null);
  
  // Stack expansion state (reserved for future use)
  // const [expandedStack, setExpandedStack] = useState(null);
  
  // Delete confirmation state (for removing cards from Off Screen)
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { zone, instanceId, cardName }
  
  // Flying card animation state
  const [flyingCards, setFlyingCards] = useState([]);
  const zoneRefs = useRef({}); // zone key → DOM element
  const cardRefs = useRef({}); // instanceId → DOM element for precise card positioning
  const gameBoardRef = useRef(null); // Game board container for overlay positioning
  const dragStartRect = useRef(null); // Store source card rect on drag start
  const prevCardRectsRef = useRef({}); // Snapshot of card positions BEFORE slide change (for FLIP animation)
  const [animatingCardIds, setAnimatingCardIds] = useState(new Set()); // Cards currently being animated (hidden via fade)
  
  const playbackRef = useRef(null);
  const currentSlide = slides[currentSlideIndex];

  // Session auto-persist: save slides to session storage on every change
  useEffect(() => {
    if (!sessionRestoredRef.current) return; // don't persist before restore attempt
    saveSessionTheater(slides);
  }, [slides, saveSessionTheater]);

  // Session restore on mount
  useEffect(() => {
    if (sessionRestoredRef.current) return;
    sessionRestoredRef.current = true;
    const session = loadSessionTheater();
    if (session?.slides && session.slides.length > 0) {
      setSlides(session.slides);
      setCurrentSlideIndex(0);
    }
  }, [loadSessionTheater]);

  // Search for cards
  const searchCards = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await fetch('/api/search-cards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          offset: 0,
          limit: 30,
          lang: language || 'EN',
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearchLoading(false);
    }
  }, [language]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchOpen && searchQuery) {
        searchCards(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchOpen, searchCards]);

  // Add card to zone
  const addCardToZone = useCallback((card, zone) => {
    const normalized = normalizeCard(card);
    // Preserve the original images structure
    normalized.images = card.images;
    const cardInstance = createCardInstance(normalized);
    
    setSlides(prev => {
      const updated = [...prev];
      const zoneCards = [...updated[currentSlideIndex].zones[zone]];
      
      // Deck zones: auto-stack onto the last card (no size limit) instead of adding separately
      const isDeckZone = ZONE_CONFIG[zone]?.faceDown;
      if (isDeckZone && zoneCards.length > 0) {
        const lastIdx = zoneCards.length - 1;
        const topCard = zoneCards[lastIdx];
        const existingStack = topCard.stackedCards || [];
        // Push former top (without its own stackedCards) into the stack bottom
        const formerTop = { ...topCard, stackedCards: undefined };
        const newStack = [...existingStack, formerTop];
        // New card becomes the top with the accumulated stack
        zoneCards[lastIdx] = { ...cardInstance, stackedCards: newStack };
      } else {
        zoneCards.push(cardInstance);
      }
      
      updated[currentSlideIndex] = {
        ...updated[currentSlideIndex],
        zones: {
          ...updated[currentSlideIndex].zones,
          [zone]: zoneCards,
        },
      };
      return updated;
    });
    
    // Only close dialog for Scene zone (single card only) - keep open for bulk adding elsewhere
    if (zone === ZONES.SCENE) {
      setSearchOpen(false);
      setTargetZone(null);
    }
  }, [currentSlideIndex]);

  // Remove card from zone - moves to Off Screen unless already there
  // For stacked cards, unstacks the top card and moves it
  const removeCardFromZone = useCallback((zone, instanceId) => {
    if (zone === ZONES.OFF_SCREEN) {
      // For Off Screen zone, show confirmation dialog
      const card = slides[currentSlideIndex].zones[zone].find(c => c.instanceId === instanceId);
      if (card) {
        setDeleteConfirm({ zone, instanceId, cardName: card.name || 'this card' });
      }
      return;
    }
    
    // Move card to Off Screen instead of deleting
    setSlides(prev => {
      const updated = [...prev];
      const cards = [...updated[currentSlideIndex].zones[zone]];
      const cardIdx = cards.findIndex(c => c.instanceId === instanceId);
      if (cardIdx === -1) return prev;
      
      const card = cards[cardIdx];
      
      if (card.stackedCards?.length > 0) {
        // Unstack: remove top card, promote next card from stack
        const stackedCards = [...card.stackedCards];
        const newTop = stackedCards.pop();
        cards[cardIdx] = { ...newTop, stackedCards: stackedCards.length > 0 ? stackedCards : undefined };
        
        // Move the removed top card to Off Screen
        const removedCard = { ...card, stackedCards: undefined };
        updated[currentSlideIndex] = {
          ...updated[currentSlideIndex],
          zones: {
            ...updated[currentSlideIndex].zones,
            [zone]: cards,
            [ZONES.OFF_SCREEN]: [...updated[currentSlideIndex].zones[ZONES.OFF_SCREEN], removedCard],
          },
        };
      } else {
        // Single card - move to Off Screen
        const updatedCard = { ...card };
        updated[currentSlideIndex] = {
          ...updated[currentSlideIndex],
          zones: {
            ...updated[currentSlideIndex].zones,
            [zone]: cards.filter(c => c.instanceId !== instanceId),
            [ZONES.OFF_SCREEN]: [...updated[currentSlideIndex].zones[ZONES.OFF_SCREEN], updatedCard],
          },
        };
      }
      
      return updated;
    });
  }, [currentSlideIndex, slides]);

  // Permanently delete card from zone (used after confirmation)
  const deleteCardFromZone = useCallback((zone, instanceId) => {
    setSlides(prev => {
      const updated = [...prev];
      updated[currentSlideIndex] = {
        ...updated[currentSlideIndex],
        zones: {
          ...updated[currentSlideIndex].zones,
          [zone]: updated[currentSlideIndex].zones[zone].filter(
            c => c.instanceId !== instanceId
          ),
        },
      };
      return updated;
    });
  }, [currentSlideIndex]);

  // Move card between zones (used by drag & drop)
  // If moving from a stack, only the top card moves - remaining cards stay as a stack
  const moveCard = useCallback((fromZone, toZone, instanceId) => {
    if (fromZone === toZone) return;
    
    setSlides(prev => {
      const updated = [...prev];
      const fromCards = [...updated[currentSlideIndex].zones[fromZone]];
      const cardIdx = fromCards.findIndex(c => c.instanceId === instanceId);
      
      if (cardIdx === -1) return prev;
      
      const card = fromCards[cardIdx];
      const stackedCards = card.stackedCards || [];
      
      // The card being moved (without stack)
      const movedCard = { ...card, stackedCards: undefined };
      
      // Handle the source zone
      if (stackedCards.length > 0) {
        // Card was part of a stack - promote the card just below top (last in array)
        const remaining = [...stackedCards];
        const newTop = remaining.pop();
        fromCards[cardIdx] = {
          ...newTop,
          stackedCards: remaining.length > 0 ? remaining : undefined,
        };
      } else {
        // Single card - just remove it
        fromCards.splice(cardIdx, 1);
      }
      
      updated[currentSlideIndex] = {
        ...updated[currentSlideIndex],
        zones: {
          ...updated[currentSlideIndex].zones,
          [fromZone]: fromCards,
          [toZone]: [...updated[currentSlideIndex].zones[toZone], movedCard],
        },
      };
      
      return updated;
    });
  }, [currentSlideIndex]);

  // Reorder card within the same zone (for horizontal dragging in fields)
  const reorderCardInZone = useCallback((zone, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    
    setSlides(prev => {
      const updated = [...prev];
      const cards = [...updated[currentSlideIndex].zones[zone]];
      const [movedCard] = cards.splice(fromIndex, 1);
      cards.splice(toIndex, 0, movedCard);
      
      updated[currentSlideIndex] = {
        ...updated[currentSlideIndex],
        zones: {
          ...updated[currentSlideIndex].zones,
          [zone]: cards,
        },
      };
      return updated;
    });
  }, [currentSlideIndex]);
  
  // Stack a card onto another card in a zone
  // When the dragged card was from a stack, only that single card stacks - rest of stack stays in source
  const stackCard = useCallback((fromZone, toZone, cardInstanceId, targetIndex) => {
    setSlides(prev => {
      const updated = [...prev];
      const fromCards = [...updated[currentSlideIndex].zones[fromZone]];
      
      // Find card to stack
      const sourceIdx = fromCards.findIndex(c => c.instanceId === cardInstanceId);
      if (sourceIdx === -1) return prev;
      
      const originalCard = fromCards[sourceIdx];
      const cardToStack = { ...originalCard, stackedCards: undefined };
      const remainingStack = originalCard.stackedCards || [];
      
      // Handle source: if it was a stack, promote card just below top (last in array)
      if (remainingStack.length > 0) {
        const rest = [...remainingStack];
        const newTop = rest.pop();
        fromCards[sourceIdx] = {
          ...newTop,
          stackedCards: rest.length > 0 ? rest : undefined,
        };
      } else {
        fromCards.splice(sourceIdx, 1);
      }
      
      // Get target zone cards
      const toCards = fromZone === toZone ? fromCards : [...updated[currentSlideIndex].zones[toZone]];
      
      // Adjust target index if same zone and source was before target (and source was removed)
      let adjIdx = targetIndex;
      if (fromZone === toZone && sourceIdx < targetIndex && remainingStack.length === 0) adjIdx--;
      
      const target = toCards[adjIdx];
      if (!target) return prev;
      
      // Check max stack size (only stacking single card now)
      const totalSize = 1 + (target.stackedCards?.length || 0) + 1;
      if (totalSize > MAX_STACK_SIZE) return prev;
      
      // Dragged card becomes new top, old top + its stack go underneath
      toCards[adjIdx] = {
        ...cardToStack,
        stackedCards: [
          ...(target.stackedCards || []),
          { ...target, stackedCards: undefined },
        ].slice(0, MAX_STACK_SIZE - 1),
      };
      
      updated[currentSlideIndex] = {
        ...updated[currentSlideIndex],
        zones: {
          ...updated[currentSlideIndex].zones,
          [fromZone]: fromCards,
          ...(fromZone !== toZone ? { [toZone]: toCards } : {}),
        },
      };
      return updated;
    });
  }, [currentSlideIndex]);

  // Capture current card positions into snapshot (call BEFORE slide change)
  const snapshotCardPositions = useCallback(() => {
    const positions = {};
    // Only snapshot refs that are still connected to the DOM (prune stale entries)
    const liveRefs = {};
    Object.entries(cardRefs.current).forEach(([id, el]) => {
      if (el && el.isConnected) {
        liveRefs[id] = el;
        const rect = el.getBoundingClientRect();
        positions[id] = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
          width: rect.width,
          height: rect.height,
          rect,
        };
      }
    });
    cardRefs.current = liveRefs;
    prevCardRectsRef.current = positions;
  }, []);

  // Trigger flying card animation using explicit from/to positions relative to game board
  const triggerFlyAnimationAt = useCallback((card, fromPos, toPos, cardWidth, cardHeight) => {
    if (!gameBoardRef.current) return;
    
    const boardRect = gameBoardRef.current.getBoundingClientRect();
    
    const flyId = `${Date.now()}-${Math.random()}`;
    const cardForAnim = { ...card, stackedCards: undefined };
    
    setAnimatingCardIds(prev => new Set([...prev, card.instanceId]));
    
    setFlyingCards(prev => [...prev, {
      id: flyId,
      card: cardForAnim,
      fromPos: {
        x: fromPos.x - boardRect.left,
        y: fromPos.y - boardRect.top,
      },
      toPos: {
        x: toPos.x - boardRect.left,
        y: toPos.y - boardRect.top,
      },
      cardWidth: cardWidth || 48,
      cardHeight: cardHeight || 79,
    }]);
  }, []);


  
  // Apply an effect to a card in the current slide
  const applyEffectToCard = useCallback((zone, instanceId, effectKey) => {
    setSlides(prev => {
      const updated = [...prev];
      const zoneCards = [...updated[currentSlideIndex].zones[zone]];
      const cardIdx = zoneCards.findIndex(c => c.instanceId === instanceId);
      if (cardIdx === -1) return prev;
      // If 'remove' effect or same effect already applied, clear it
      const current = zoneCards[cardIdx].effect;
      const newEffect = (effectKey === 'remove' || current === effectKey) ? undefined : effectKey;
      zoneCards[cardIdx] = { ...zoneCards[cardIdx], effect: newEffect };
      updated[currentSlideIndex] = {
        ...updated[currentSlideIndex],
        zones: { ...updated[currentSlideIndex].zones, [zone]: zoneCards },
      };
      return updated;
    });
  }, [currentSlideIndex]);

  // Drag and drop handlers
  // When dragging from a stack, we only drag the top card (without stackedCards)
  const handleDragStart = useCallback((e, card, zone, index) => {
    // Extract just the top card, remove stackedCards reference
    const singleCard = { ...card, stackedCards: undefined };
    // Store in ref immediately (no re-render, preserves drag element)
    dragDataRef.current = { card: singleCard, zone, index };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', card.instanceId);
    // Store source position for flying animation
    dragStartRect.current = e.currentTarget.getBoundingClientRect();
    // Defer state update so browser captures drag element before React re-renders
    requestAnimationFrame(() => {
      setDraggedCard(singleCard);
      setDragSourceZone(zone);
      setDragSourceIndex(index);
    });
  }, []);
  
  const handleDragEnd = useCallback(() => {
    dragDataRef.current = { card: null, zone: null, index: null };
    setDraggedCard(null);
    setDragSourceZone(null);
    setDropTargetZone(null);
    setDragSourceIndex(null);
    setDropTargetIndex(null);
    dragStartRect.current = null;
  }, []);
  
  const handleDragOver = useCallback((e, zone, index = null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetZone(zone);
    setDropTargetIndex(index);
  }, []);
  
  const handleDragLeave = useCallback(() => {
    setDropTargetZone(null);
    setDropTargetIndex(null);
  }, []);
  
  const handleDrop = useCallback((e, targetZone, targetIndex = null) => {
    e.preventDefault();
    // Read from ref (authoritative drag data, not affected by render timing)
    const { card: refCard, zone: refZone, index: refIndex } = dragDataRef.current;
    
    if (!refCard || !refZone) return;
    
    // Same zone - reorder horizontally
    if (refZone === targetZone && targetIndex !== null && refIndex !== null) {
      reorderCardInZone(targetZone, refIndex, targetIndex);
    }
    // Different zone - move card (animations only happen during playback)
    else if (refZone !== targetZone) {
      moveCard(refZone, targetZone, refCard.instanceId);
    }
    
    dragDataRef.current = { card: null, zone: null, index: null };
    setDraggedCard(null);
    setDragSourceZone(null);
    setDropTargetZone(null);
    setDragSourceIndex(null);
    setDropTargetIndex(null);
    dragStartRect.current = null;
  }, [moveCard, reorderCardInZone]);

  // Drop on a specific card to stack onto it
  const handleDropOnCard = useCallback((e, targetZone, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    // Read from ref (authoritative drag data)
    const { card: refCard, zone: refZone } = dragDataRef.current;
    
    if (refCard && refZone) {
      stackCard(refZone, targetZone, refCard.instanceId, targetIndex);
    }
    
    dragDataRef.current = { card: null, zone: null, index: null };
    setDraggedCard(null);
    setDragSourceZone(null);
    setDropTargetZone(null);
    setDragSourceIndex(null);
    setDropTargetIndex(null);
    dragStartRect.current = null;
  }, [stackCard]);

  // Slide management
  const addSlide = useCallback(() => {
    const newSlide = createEmptySlide();
    // Copy cards from current slide to new slide (maintain state)
    if (currentSlide) {
      newSlide.zones = JSON.parse(JSON.stringify(currentSlide.zones));
    }
    setSlides(prev => {
      const updated = [...prev];
      updated.splice(currentSlideIndex + 1, 0, newSlide);
      return updated;
    });
    setCurrentSlideIndex(prev => prev + 1);
  }, [currentSlide, currentSlideIndex]);

  const duplicateSlide = useCallback(() => {
    const duplicate = {
      ...JSON.parse(JSON.stringify(currentSlide)),
      id: Date.now(),
    };
    setSlides(prev => {
      const updated = [...prev];
      updated.splice(currentSlideIndex + 1, 0, duplicate);
      return updated;
    });
    setCurrentSlideIndex(prev => prev + 1);
  }, [currentSlide, currentSlideIndex]);

  const deleteSlide = useCallback(() => {
    if (slides.length === 1) return;
    setSlides(prev => prev.filter((_, i) => i !== currentSlideIndex));
    setCurrentSlideIndex(prev => Math.max(0, prev - 1));
  }, [currentSlideIndex, slides.length]);

  // Navigation
  const goToSlide = useCallback((index) => {
    setCurrentSlideIndex(Math.max(0, Math.min(slides.length - 1, index)));
  }, [slides.length]);

  const nextSlide = useCallback(() => {
    goToSlide(currentSlideIndex + 1);
  }, [currentSlideIndex, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide(currentSlideIndex - 1);
  }, [currentSlideIndex, goToSlide]);

  // Caption management
  const updateCaption = useCallback((caption) => {
    setSlides(prev => {
      const updated = [...prev];
      updated[currentSlideIndex] = {
        ...updated[currentSlideIndex],
        caption,
      };
      return updated;
    });
  }, [currentSlideIndex]);

  const startEditCaption = () => {
    setCaptionText(currentSlide?.caption || '');
    setEditingCaption(true);
  };

  const saveCaption = () => {
    updateCaption(captionText);
    setEditingCaption(false);
  };

  // Playback - snapshots card positions before advancing slide
  useEffect(() => {
    if (isPlaying) {
      playbackRef.current = setInterval(() => {
        // Snapshot ALL card positions before the slide changes
        snapshotCardPositions();
        
        setCurrentSlideIndex(prev => {
          if (prev >= slides.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, playbackSpeed);
    } else {
      if (playbackRef.current) {
        clearInterval(playbackRef.current);
      }
    }
    
    return () => {
      if (playbackRef.current) {
        clearInterval(playbackRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, slides.length, snapshotCardPositions]);

  // Animate cards during playback - detect ALL card movements between slides
  // Uses FLIP technique: positions were snapshotted BEFORE the slide changed,
  // then after React renders the new slide we read new positions from the DOM.
  // useLayoutEffect runs BEFORE browser paint, so we hide moving cards before they flicker.
  const prevSlideIndexRef = useRef(currentSlideIndex);
  useLayoutEffect(() => {
    if (!isPlaying) {
      prevSlideIndexRef.current = currentSlideIndex;
      return;
    }
    
    const prevIndex = prevSlideIndexRef.current;
    if (prevIndex === currentSlideIndex) return;
    
    // Slide 1 (index 0) is the default/initial state — no animations into it
    if (currentSlideIndex === 0) {
      prevSlideIndexRef.current = currentSlideIndex;
      return;
    }
    
    const prevSlide = slides[prevIndex];
    const currSlide = slides[currentSlideIndex];
    if (!prevSlide || !currSlide) {
      prevSlideIndexRef.current = currentSlideIndex;
      return;
    }
    
    // Build a map of instanceId → zone for previous and current slides
    const prevCardZones = {};
    const currCardZones = {};
    
    Object.keys(prevSlide.zones).forEach(zone => {
      (prevSlide.zones[zone] || []).forEach(card => {
        prevCardZones[card.instanceId] = zone;
        if (card.stackedCards) {
          card.stackedCards.forEach(sc => {
            prevCardZones[sc.instanceId] = zone;
          });
        }
      });
    });
    
    Object.keys(currSlide.zones).forEach(zone => {
      (currSlide.zones[zone] || []).forEach(card => {
        currCardZones[card.instanceId] = zone;
        if (card.stackedCards) {
          card.stackedCards.forEach(sc => {
            currCardZones[sc.instanceId] = zone;
          });
        }
      });
    });
    
    // Collect all card instanceIds present in both slides
    const allCardIds = new Set([
      ...Object.keys(prevCardZones),
      ...Object.keys(currCardZones),
    ]);
    
    // Find card data helper - returns { card, zoneIndex, stackIndex } for precise position tracking
    const findCardPositionInSlide = (slide, instanceId) => {
      for (const zone of Object.keys(slide.zones)) {
        const cards = slide.zones[zone] || [];
        for (let i = 0; i < cards.length; i++) {
          if (cards[i].instanceId === instanceId) {
            return { card: cards[i], zoneIndex: i, stackIndex: -1 }; // top-level card
          }
          if (cards[i].stackedCards) {
            const si = cards[i].stackedCards.findIndex(sc => sc.instanceId === instanceId);
            if (si !== -1) {
              return { card: cards[i].stackedCards[si], zoneIndex: i, stackIndex: si };
            }
          }
        }
      }
      return null;
    };
    
    // Cards that actually moved: changed zone, changed position within zone, or changed stack membership
    const candidateCards = [];
    allCardIds.forEach(instanceId => {
      const prevZone = prevCardZones[instanceId];
      const currZone = currCardZones[instanceId];
      if (!prevZone || !currZone) return; // card added or removed — don't animate
      
      const prevPos = findCardPositionInSlide(prevSlide, instanceId);
      const currPos = findCardPositionInSlide(currSlide, instanceId);
      if (!prevPos || !currPos) return;
      
      // Only animate if the card changed zones or moved to a different lane (zoneIndex).
      // Stack promotion within the same lane (e.g. top card removed, card beneath uncovered)
      // should NOT animate — the card barely moves visually.
      const zoneChanged = prevZone !== currZone;
      const indexChanged = prevPos.zoneIndex !== currPos.zoneIndex;
      
      if (zoneChanged || indexChanged) {
        candidateCards.push({
          card: currPos.card,
          instanceId,
          fromZone: prevZone,
          toZone: currZone,
        });
      }
    });
    
    // Trigger flying animations — compare snapshotted vs new position
    // This catches both cross-zone AND within-zone moves (e.g. stack to stack)
    if (candidateCards.length > 0) {
      const savedPositions = prevCardRectsRef.current;
      const MOVE_THRESHOLD = 8; // px — ignore tiny sub-pixel shifts
      
      // Pre-hide ALL candidate cards immediately to prevent flicker
      // (cards appear at destination before animation starts)
      const candidateIds = candidateCards.map(c => c.instanceId);
      setAnimatingCardIds(prev => {
        const next = new Set(prev);
        candidateIds.forEach(id => next.add(id));
        return next;
      });
      
      // Wait two frames so React has fully rendered the new slide and layout is stable
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const animatedIds = new Set();
          candidateCards.forEach(({ card, instanceId, fromZone, toZone }) => {
            // FROM: use snapshotted position (where card WAS before slide change)
            const savedFrom = savedPositions[instanceId];
            let fromPos;
            if (savedFrom) {
              fromPos = { x: savedFrom.x, y: savedFrom.y, width: savedFrom.width, height: savedFrom.height };
            } else if (zoneRefs.current[fromZone]) {
              const r = zoneRefs.current[fromZone].getBoundingClientRect();
              fromPos = { x: r.left + r.width / 2, y: r.top + r.height / 2, width: 48, height: 79 };
            } else return;
            
            // TO: read current DOM position (where card IS now in the new slide)
            const toEl = cardRefs.current[instanceId];
            let toPos;
            if (toEl && toEl.isConnected) {
              const r = toEl.getBoundingClientRect();
              toPos = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
            } else if (zoneRefs.current[toZone]) {
              const r = zoneRefs.current[toZone].getBoundingClientRect();
              toPos = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
            } else return;
            
            // Only animate if the card actually moved a meaningful distance
            const dx = toPos.x - fromPos.x;
            const dy = toPos.y - fromPos.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < MOVE_THRESHOLD) return;
            
            animatedIds.add(instanceId);
            triggerFlyAnimationAt(card, fromPos, toPos, fromPos.width, fromPos.height);
          });
          
          // Un-hide cards that didn't actually need animation (below threshold)
          const notAnimated = candidateIds.filter(id => !animatedIds.has(id));
          if (notAnimated.length > 0) {
            setAnimatingCardIds(prev => {
              const next = new Set(prev);
              notAnimated.forEach(id => next.delete(id));
              return next;
            });
          }
        });
      });
    }
    
    prevSlideIndexRef.current = currentSlideIndex;
  }, [currentSlideIndex, isPlaying, slides, triggerFlyAnimationAt]);

  const togglePlayback = () => {
    if (currentSlideIndex >= slides.length - 1) {
      setCurrentSlideIndex(0);
    }
    setIsPlaying(prev => !prev);
  };

  // Export as GIF - captures each slide with card images, animations, and captions
  const [isExporting, setIsExporting] = useState(false);
  const exportGif = useCallback(async () => {
    if (!gameBoardRef.current || slides.length < 2) {
      alert('Need at least 2 slides to export a GIF.');
      return;
    }
    
    setIsExporting(true);
    
    try {
      const boardEl = gameBoardRef.current;
      const rect = boardEl.getBoundingClientRect();
      const width = Math.round(rect.width);
      const height = Math.round(rect.height);
      
      const gif = new GIF({
        workers: 2,
        quality: 10,
        width,
        height,
        workerScript: '/gif.worker.js',
      });
      
      // ── Phase 1: Pre-load all card images as data URLs via server proxy ──
      const imageDataUrls = {};   // originalUrl → dataUrl
      const imageElements = {};   // originalUrl → Image object (for canvas drawing)
      
      const allImageUrls = new Set();
      slides.forEach(slide => {
        Object.values(slide.zones).forEach(zoneCards => {
          (zoneCards || []).forEach(card => {
            const url = getCardImageUrl(card, 'thumbnail');
            if (url) allImageUrls.add(url);
            if (card.stackedCards) {
              card.stackedCards.forEach(sc => {
                const scUrl = getCardImageUrl(sc, 'thumbnail');
                if (scUrl) allImageUrls.add(scUrl);
              });
            }
          });
        });
      });
      
      // Fetch each image via server proxy, convert to data URL
      // Skip local paths (like card-back) — they don't need proxying
      await Promise.all([...allImageUrls].map(async (url) => {
        try {
          // Local paths (start with /) can be fetched directly
          const fetchUrl = url.startsWith('/') && !url.startsWith('//') 
            ? url 
            : `/api/image?url=${encodeURIComponent(url)}`;
          const resp = await fetch(fetchUrl);
          if (!resp.ok) throw new Error(resp.statusText);
          const blob = await resp.blob();
          const dataUrl = await new Promise(resolve => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          imageDataUrls[url] = dataUrl;
          // Also pre-load as Image for canvas compositing
          const img = new window.Image();
          img.src = dataUrl;
          await new Promise(r => { img.onload = r; img.onerror = r; });
          imageElements[url] = img;
        } catch (e) {
          console.warn('Pre-load failed for', url, e);
        }
      }));
      
      // Pre-load card-back as Image for canvas compositing
      const cardBackImg = new window.Image();
      cardBackImg.src = CARD_BACK_IMAGE;
      await new Promise(r => { cardBackImg.onload = r; cardBackImg.onerror = r; });
      // Register card-back in imageElements so getCardImg finds it for placeholder cards
      imageElements[CARD_BACK_IMAGE] = cardBackImg;
      
      // Swap all <img> srcs inside the board to data URLs, returns restore fn
      const swapImages = () => {
        const swaps = [];
        boardEl.querySelectorAll('img').forEach(el => {
          const origSrc = el.getAttribute('src') || '';
          // Match by checking if any cached URL is contained in the src
          for (const [url, dataUrl] of Object.entries(imageDataUrls)) {
            if (origSrc === url || origSrc === getProxyImageUrl(url)) {
              el.setAttribute('src', dataUrl);
              swaps.push({ el, origSrc });
              break;
            }
          }
        });
        return () => swaps.forEach(({ el, origSrc }) => el.setAttribute('src', origSrc));
      };
      
      // Helper: capture the board with images swapped
      const captureBoard = async () => {
        const restore = swapImages();
        await new Promise(r => setTimeout(r, 50)); // let swapped srcs settle
        const canvas = await html2canvas(boardEl, {
          backgroundColor: null,
          scale: 1,
          useCORS: true,
          allowTaint: true,
          width,
          height,
        });
        restore();
        return canvas;
      };
      
      // Wait for React render + layout to stabilize
      const waitForRender = (ms = 200) =>
        new Promise(r => requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(r, ms))));
      
      // ── Phase 2: Determine card movements between slides ──
      const findMovedCards = (prevSlide, currSlide) => {
        const prevMap = {};
        const currMap = {};
        Object.keys(prevSlide.zones).forEach(zone => {
          (prevSlide.zones[zone] || []).forEach((card, idx) => {
            prevMap[card.instanceId] = { zone, zoneIndex: idx };
            (card.stackedCards || []).forEach(sc => { prevMap[sc.instanceId] = { zone, zoneIndex: idx }; });
          });
        });
        Object.keys(currSlide.zones).forEach(zone => {
          (currSlide.zones[zone] || []).forEach((card, idx) => {
            currMap[card.instanceId] = { zone, zoneIndex: idx };
            (card.stackedCards || []).forEach(sc => { currMap[sc.instanceId] = { zone, zoneIndex: idx }; });
          });
        });
        const moved = [];
        const allIds = new Set([...Object.keys(prevMap), ...Object.keys(currMap)]);
        allIds.forEach(id => {
          const p = prevMap[id], c = currMap[id];
          if (!p || !c) return;
          if (p.zone !== c.zone || p.zoneIndex !== c.zoneIndex) moved.push(id);
        });
        return moved;
      };
      
      // Look up the card image (Image object) for compositing
      const getCardImg = (slide, instanceId) => {
        for (const zone of Object.keys(slide.zones)) {
          for (const card of (slide.zones[zone] || [])) {
            const match = card.instanceId === instanceId ? card :
              (card.stackedCards || []).find(sc => sc.instanceId === instanceId);
            if (match) {
              if (ZONE_CONFIG[zone]?.faceDown) return cardBackImg;
              const url = getCardImageUrl(match, 'thumbnail');
              return url ? (imageElements[url] || null) : null;
            }
          }
        }
        return null;
      };
      
      // ── Phase 3: Save state & produce frames ──
      const savedIndex = currentSlideIndex;
      const savedPlaying = isPlaying;
      if (savedPlaying) setIsPlaying(false);
      
      const ANIM_FRAMES = 9;                          // frames during each transition animation
      const ANIM_DURATION = 450;                       // ms, matches FlyingCard duration
      const ANIM_FRAME_DELAY = Math.round(ANIM_DURATION / ANIM_FRAMES); // ~50ms per frame
      
      // Capture first slide
      setCurrentSlideIndex(0);
      await waitForRender();
      let prevCanvas = await captureBoard();
      gif.addFrame(prevCanvas, { delay: playbackSpeed, copy: true });
      
      for (let i = 1; i < slides.length; i++) {
        const prevSlide = slides[i - 1];
        const currSlide = slides[i];
        const movedIds = findMovedCards(prevSlide, currSlide);
        
        if (movedIds.length > 0) {
          // Record FROM positions (current DOM) before switching slide
          const boardR = boardEl.getBoundingClientRect();
          const fromPositions = {};
          movedIds.forEach(id => {
            const el = cardRefs.current[id];
            if (el && el.isConnected) {
              const r = el.getBoundingClientRect();
              fromPositions[id] = {
                x: r.left + r.width / 2 - boardR.left,
                y: r.top + r.height / 2 - boardR.top,
                width: r.width,
                height: r.height,
              };
            }
          });
          
          // Switch slide & hide the moving cards so destination shows empty slots
          setCurrentSlideIndex(i);
          setAnimatingCardIds(new Set(movedIds));
          await waitForRender();
          
          // Record TO positions (new DOM)
          const boardR2 = boardEl.getBoundingClientRect();
          const toPositions = {};
          movedIds.forEach(id => {
            const el = cardRefs.current[id];
            if (el && el.isConnected) {
              const r = el.getBoundingClientRect();
              toPositions[id] = {
                x: r.left + r.width / 2 - boardR2.left,
                y: r.top + r.height / 2 - boardR2.top,
              };
            }
          });
          
          // Capture the destination frame (with moving cards hidden)
          const destCanvas = await captureBoard();
          
          // Use the PREVIOUS slide's canvas as the starting base so cards
          // don't visually disappear when the board switches to the new state.
          // Crossfade from prevCanvas → destCanvas over the animation duration
          // while compositing the flying cards on top.
          for (let f = 0; f < ANIM_FRAMES; f++) {
            const t = f / (ANIM_FRAMES - 1);  // 0 → 1
            const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // ease-in-out quad
            
            const frameCanvas = document.createElement('canvas');
            frameCanvas.width = width;
            frameCanvas.height = height;
            const ctx = frameCanvas.getContext('2d');
            
            // Crossfade: draw previous slide fully, then overlay destination with increasing opacity
            ctx.drawImage(prevCanvas, 0, 0);
            ctx.globalAlpha = eased;
            ctx.drawImage(destCanvas, 0, 0);
            ctx.globalAlpha = 1;
            
            // Draw each moving card at its interpolated arc position
            movedIds.forEach(id => {
              const from = fromPositions[id];
              const to = toPositions[id];
              const img = getCardImg(currSlide, id);
              if (!from || !to || !img) return;
              
              const x = from.x + (to.x - from.x) * eased;
              const y = from.y + (to.y - from.y) * eased;
              
              // Arc lift
              const dx = to.x - from.x;
              const dy = to.y - from.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              const arcHeight = Math.min(dist * 0.15 + 15, 60);
              const arcY = -arcHeight * Math.sin(Math.PI * t);
              
              // Scale: 1 → 1.1 → 1
              const scale = 1 + 0.1 * Math.sin(Math.PI * t);
              const cw = (from.width || 48) * scale;
              const ch = (from.height || 79) * scale;
              
              ctx.save();
              ctx.shadowColor = 'rgba(0,0,0,0.4)';
              ctx.shadowBlur = 12;
              ctx.drawImage(img, x - cw / 2, y + arcY - ch / 2, cw, ch);
              ctx.restore();
            });
            
            gif.addFrame(frameCanvas, { delay: ANIM_FRAME_DELAY, copy: true });
          }
          
          // Un-hide cards and capture the settled destination
          setAnimatingCardIds(new Set());
          await waitForRender(100);
          const holdDelay = Math.max(playbackSpeed - ANIM_DURATION, 200);
          prevCanvas = await captureBoard();
          gif.addFrame(prevCanvas, { delay: holdDelay, copy: true });
        } else {
          // No cards moved — just capture the new static slide
          setCurrentSlideIndex(i);
          await waitForRender();
          prevCanvas = await captureBoard();
          gif.addFrame(prevCanvas, { delay: playbackSpeed, copy: true });
        }
      }
      
      // Restore original state
      setCurrentSlideIndex(savedIndex);
      setAnimatingCardIds(new Set());
      
      // Render and download
      gif.on('finished', (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `theater-${Date.now()}.gif`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setIsExporting(false);
      });
      
      gif.render();
    } catch (err) {
      console.error('GIF export failed:', err);
      alert('GIF export failed. Check console for details.');
      setIsExporting(false);
    }
  }, [slides, currentSlideIndex, isPlaying, playbackSpeed]);

  // Open search dialog for a specific zone - preserves previous search state
  const openCardSearch = (zone) => {
    setTargetZone(zone);
    // Don't reset searchQuery and searchResults - preserves previous search
    setSearchOpen(true);
  };

  // Card thumbnail component with drag support and stacking visual
  const CardThumbnail = ({ card, zone, index = null, showFaceDown = false, cardSize = null }) => {
    const [showActions, setShowActions] = useState(false);
    const [isDropHover, setIsDropHover] = useState(false);
    const [isEffectDropHover, setIsEffectDropHover] = useState(false);
    const isScene = card.feature === 'scene' || card.feature?.value === 'scene';
    const isFading = card.fadeState === 'entering' || card.fadeState === 'leaving';
    
    const stackedCards = card.stackedCards || [];
    const allCards = [...stackedCards, card]; // bottom to top, card is the top
    const isStack = allCards.length > 1;
    // Use provided cardSize or fall back to defaults
    const cardW = cardSize?.width || (isScene ? 80 : 48);
    const cardH = cardSize?.height || (isScene ? 67 : 79);
    // Deck zones (faceDown): no peek — cards stack flat like a real deck
    const stackHeight = showFaceDown ? cardH : cardH + stackedCards.length * STACK_PEEK;
    
    // Can this card be a stack target? Only field zones (with size limit) and deck zones (unlimited)
    const isDeckZone = ZONE_CONFIG[zone]?.faceDown;
    const isFieldZone = zone === ZONES.PLAYER_FIELD || zone === ZONES.OPPONENT_FIELD;
    const canStack = draggedCard && draggedCard.instanceId !== card.instanceId && (isDeckZone || (isFieldZone && allCards.length < MAX_STACK_SIZE));
    // Can accept an effect drop?
    const canAcceptEffect = draggingEffect && !showFaceDown;
    
    // Get the effect config for the top card
    const topCardEffect = card.effect ? CARD_EFFECTS[card.effect] : null;
    
    return (
      <div 
        className={`relative group cursor-grab active:cursor-grabbing
          ${isFading ? (card.fadeState === 'entering' ? 'animate-fade-in' : 'animate-fade-out') : ''}
          ${isDropHover && canStack ? 'ring-2 ring-purple-400 rounded-lg' : ''}
          ${isEffectDropHover && canAcceptEffect ? 'ring-2 ring-yellow-400 rounded-lg' : ''}`}
        style={{ width: cardW, height: stackHeight }}
        draggable
        onDragStart={(e) => {
          handleDragStart(e, card, zone, index);
        }}
        onDragEnd={handleDragEnd}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
        onDragOver={(e) => {
          if (canAcceptEffect) {
            e.preventDefault();
            e.stopPropagation();
            setIsEffectDropHover(true);
          } else if (canStack) {
            e.preventDefault();
            e.stopPropagation();
            setIsDropHover(true);
          }
        }}
        onDragLeave={() => { setIsDropHover(false); setIsEffectDropHover(false); }}
        onDrop={(e) => {
          if (canAcceptEffect && draggingEffect) {
            e.preventDefault();
            e.stopPropagation();
            setIsEffectDropHover(false);
            applyEffectToCard(zone, card.instanceId, draggingEffect);
            setDraggingEffect(null);
          } else if (canStack) {
            setIsDropHover(false);
            handleDropOnCard(e, zone, index);
          }
        }}
      >
        {/* Render stacked cards bottom-anchored: bottom card (i=0) at bottom, 
            each higher card peeks upward. When top card leaves, bottom stays fixed. */}
        {allCards.map((c, i) => {
          const cImageUrl = showFaceDown ? null : getCardImageUrl(c, 'thumbnail');
          const isCardAnimating = animatingCardIds.has(c.instanceId);
          // Deck zones: all cards at offset 0 (flat stack); other zones: peek up
          const bottomOffset = showFaceDown ? 0 : i * STACK_PEEK;
          const isTopCard = i === allCards.length - 1;
          const cardEffect = isTopCard && topCardEffect;
          
          return (
            <div
              key={c.instanceId}
              className={`absolute left-0 ${isCardAnimating ? 'opacity-0' : ''}`}
              style={{ bottom: bottomOffset, zIndex: i, width: cardW, height: cardH }}
              ref={(el) => { if (el) cardRefs.current[c.instanceId] = el; }}
            >
              {/* Glow effect layer — rendered behind the card */}
              {cardEffect && (
                <div
                  className="absolute -inset-1 rounded-lg opacity-75 animate-pulse"
                  style={{
                    boxShadow: cardEffect.shadow,
                    background: `linear-gradient(135deg, ${cardEffect.color}33, ${cardEffect.color}66, ${cardEffect.color}33)`,
                    zIndex: -1,
                  }}
                />
              )}
              <div 
                className={`relative rounded overflow-hidden bg-slate-700 flex items-center justify-center shadow-md ${
                  cardEffect ? `border-2 ${cardEffect.border}` : 'border border-white/20'
                }`}
                style={{ width: cardW, height: cardH }}
              >
                {showFaceDown ? (
                  <img
                    src={CARD_BACK_IMAGE}
                    alt="Card back"
                    className="w-full h-full object-cover"
                  />
                ) : cImageUrl ? (
                  <img
                    src={getProxyImageUrl(cImageUrl)}
                    alt={c.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <Image className="h-5 w-5 text-slate-400" />
                )}
              </div>
            </div>
          );
        })}
        
        {/* Stack count badge - hidden during playback/export */}
        {isStack && !isPlaying && !isExporting && (
          <div className="absolute -top-1 -right-1 z-30 bg-purple-600 rounded-full h-5 w-5 flex items-center justify-center">
            <span className="text-[10px] font-bold text-white">{allCards.length}</span>
          </div>
        )}
        
        {/* Effect indicator badge */}
        {topCardEffect && !isPlaying && !isExporting && (
          <div 
            className="absolute -top-1 -left-1 z-30 rounded-full h-4 w-4 flex items-center justify-center cursor-pointer"
            style={{ backgroundColor: topCardEffect.color }}
            onClick={(e) => {
              e.stopPropagation();
              applyEffectToCard(zone, card.instanceId, card.effect); // toggle off
            }}
            title="Click to remove effect"
          >
            <Sparkles className="h-2.5 w-2.5 text-white" />
          </div>
        )}
        
        {showActions && !showFaceDown && (
          <div className="absolute -top-2 -right-2 z-20" style={{ zIndex: allCards.length + 10 }}>
            <Button
              size="icon"
              variant="destructive"
              className="h-5 w-5 rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                removeCardFromZone(zone, card.instanceId);
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Deck zone - shows face-down cards with stacking
  const DeckZone = ({ zone, cards, className = '' }) => {
    const isFaceDown = ZONE_CONFIG[zone]?.faceDown;
    const isDropTarget = dropTargetZone === zone;
    
    return (
      <div 
        className={`border rounded-lg p-2 transition-all duration-200
          ${ZONE_CONFIG[zone]?.color} ${className}
          ${isDropTarget ? 'ring-2 ring-green-400 bg-green-900/20' : 'border-white/20'}`}
        ref={(el) => { zoneRefs.current[zone] = el; }}
        onDragOver={(e) => handleDragOver(e, zone)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, zone)}
      >
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-medium text-white/70 uppercase tracking-wider">
            {ZONE_CONFIG[zone]?.label}
          </span>
          {!isPlaying && !isExporting && (
            <div className="flex items-center gap-1">
              <Badge variant="secondary" className="text-[10px] h-4 px-1">
                {cards.length}
              </Badge>
              <Button
                size="icon"
                variant="ghost"
                className="h-5 w-5 text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => openCardSearch(zone)}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-1 min-h-[50px] items-center justify-center">
          {cards.length > 0 ? (
            cards.map((card, idx) => (
              <CardThumbnail 
                key={card.instanceId} 
                card={card} 
                zone={zone}
                index={idx}
                showFaceDown={isFaceDown}
              />
            ))
          ) : (
            <div className="text-[10px] text-white/40 flex items-center justify-center w-full">
              {t('Empty')}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Expanded zone - for fields, hands, and scene
  const ExpandedZone = ({ zone, cards, className = '', minHeight = '80px' }) => {
    const isDropTarget = dropTargetZone === zone;
    
    return (
      <div 
        className={`border rounded-lg p-3 transition-all duration-200
          ${ZONE_CONFIG[zone]?.color} ${className}
          ${isDropTarget ? 'ring-2 ring-green-400 bg-green-900/20' : 'border-white/20'}`}
        ref={(el) => { zoneRefs.current[zone] = el; }}
        onDragOver={(e) => handleDragOver(e, zone)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, zone)}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-white/80 uppercase tracking-wider">
            {ZONE_CONFIG[zone]?.label}
          </span>
          {!isPlaying && !isExporting && (
            <div className="flex items-center gap-1">
              <Badge variant="secondary" className="text-xs">
                {cards.length}
              </Badge>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => openCardSearch(zone)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 justify-center" style={{ minHeight }}>
          {cards.length > 0 ? (
            cards.map((card, index) => (
              <CardThumbnail key={card.instanceId} card={card} zone={zone} index={index} />
            ))
          ) : (
            <div className="text-xs text-white/40 flex items-center">
              {t('Drop cards here')}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Field zone - for player/opponent fields with horizontal lanes and drag reordering
  const FieldZone = ({ zone, cards, className = '' }) => {
    const isDropTarget = dropTargetZone === zone;
    const isDraggingInZone = dragSourceZone === zone;
    const containerRef = useRef(null);
    const [cardSize, setCardSize] = useState({ width: 64, height: 88 });
    const MIN_SLOTS = 6; // Used to calculate card sizing to fit 6 cards
    
    // Calculate card size based on available container space
    useEffect(() => {
      if (!containerRef.current) return;
      const updateSize = () => {
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        // Reserve space for gaps (6 cards = 5 gaps of 8px)
        const availableWidth = rect.width - (MIN_SLOTS - 1) * 8;
        const availableHeight = rect.height - 8; // padding
        // Card aspect ratio is 220:305 (standard card)
        const cardRatio = 220 / 305;
        // Calculate max card width that fits 6 cards horizontally
        const maxWidthByCount = availableWidth / MIN_SLOTS;
        // Calculate max card width based on height constraint
        const maxWidthByHeight = availableHeight * cardRatio;
        // Use the smaller to ensure cards fit both ways
        const cardWidth = Math.min(maxWidthByCount, maxWidthByHeight, 100); // cap at 100px
        const cardHeight = cardWidth / cardRatio;
        setCardSize({ width: Math.max(48, cardWidth), height: Math.max(79, cardHeight) });
      };
      updateSize();
      const observer = new ResizeObserver(updateSize);
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }, []);
    
    return (
      <div 
        className={`border rounded-lg p-3 transition-all duration-200 flex flex-col
          ${ZONE_CONFIG[zone]?.color} ${className}
          ${isDropTarget && !isDraggingInZone ? 'ring-2 ring-green-400 bg-green-900/20' : 'border-white/20'}`}
        ref={(el) => { zoneRefs.current[zone] = el; }}
        onDragOver={(e) => handleDragOver(e, zone)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, zone)}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-white/80 uppercase tracking-wider">
            {ZONE_CONFIG[zone]?.label}
          </span>
          {!isPlaying && !isExporting && (
            <div className="flex items-center gap-1">
              <Badge variant="secondary" className="text-xs">
                {cards.length}
              </Badge>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => openCardSearch(zone)}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        
        {/* Field container - cards left aligned, drop zone appears on right when dragging */}
        <div 
          ref={containerRef}
          className="flex gap-2 flex-1 items-end justify-start"
        >
          {/* Cards */}
          {cards.map((card, index) => (
            <React.Fragment key={card.instanceId}>
              {/* Drop zone before card (only show when dragging in same zone for reordering) */}
              {draggedCard && dragSourceZone === zone && (
                <div
                  className={`rounded transition-all ${
                    dropTargetIndex === index ? 'bg-green-500/50 w-2' : 'bg-transparent w-0'
                  }`}
                  style={{ height: cardSize.height }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDragOver(e, zone, index);
                  }}
                  onDrop={(e) => {
                    e.stopPropagation();
                    handleDrop(e, zone, index);
                  }}
                />
              )}
              <div style={{ width: cardSize.width, height: cardSize.height + (card.stackedCards?.length || 0) * STACK_PEEK, flexShrink: 0 }}>
                <CardThumbnail card={card} zone={zone} index={index} cardSize={cardSize} />
              </div>
            </React.Fragment>
          ))}
          
          {/* Drop zone at the end - appears when dragging any card */}
          {draggedCard && (
            <div
              className={`rounded border-2 border-dashed transition-all flex-shrink-0 ${
                dropTargetIndex === cards.length || (dropTargetZone === zone && dropTargetIndex === null) 
                  ? 'border-green-400 bg-green-900/30' : 'border-white/20'
              }`}
              style={{ width: cardSize.width, height: cardSize.height }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDragOver(e, zone, cards.length);
              }}
              onDrop={(e) => {
                e.stopPropagation();
                handleDrop(e, zone, cards.length);
              }}
            />
          )}
          
          {/* Empty state when no cards and not dragging */}
          {cards.length === 0 && !draggedCard && (
            <div className="text-xs text-white/40 flex items-center justify-center w-full py-4">
              {t('Drop cards here')}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Scene zone - smaller singular zone for scene cards only
  const SceneZone = ({ cards, className = '' }) => {
    const zone = ZONES.SCENE;
    const isDropTarget = dropTargetZone === zone;
    const card = cards[0]; // Only show first card since scene is singular
    
    return (
      <div 
        className={`border-2 rounded-lg p-2 transition-all duration-200 mx-auto
          ${ZONE_CONFIG[zone]?.color} ${className}
          ${isDropTarget ? 'ring-2 ring-amber-400 bg-amber-900/30' : 'border-amber-600/50'}`}
        style={{ width: 'fit-content', minWidth: '140px' }}
        ref={(el) => { zoneRefs.current[zone] = el; }}
        onDragOver={(e) => handleDragOver(e, zone)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, zone)}
      >
        <div className="flex items-center justify-center gap-2 mb-1">
          <span className="text-[10px] font-medium text-amber-200/80 uppercase tracking-wider">
            {t('Scene Area')}
          </span>
          {!isPlaying && !card && (
            <Button
              size="icon"
              variant="ghost"
              className="h-5 w-5 text-amber-200/70 hover:text-amber-200 hover:bg-amber-900/30"
              onClick={() => openCardSearch(zone)}
            >
              <Plus className="h-3 w-3" />
            </Button>
          )}
        </div>
        <div className="flex justify-center items-center min-h-[50px]">
          {card ? (
            <CardThumbnail card={card} zone={zone} index={0} />
          ) : (
            <div className="w-20 h-14 rounded border-2 border-dashed border-amber-600/30 flex items-center justify-center">
              <span className="text-[10px] text-amber-200/40">{t('Drop scene')}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Off-screen zone - neutral holding area for cards not in play
  const OffScreenZone = ({ cards, className = '' }) => {
    const zone = ZONES.OFF_SCREEN;
    const isDropTarget = dropTargetZone === zone;
    
    return (
      <div 
        className={`border rounded-lg p-3 h-full transition-all duration-200 bg-card
          ${isDropTarget ? 'ring-2 ring-green-400 bg-green-900/20' : 'border-border'} ${className}`}
        ref={(el) => { zoneRefs.current[zone] = el; }}
        onDragOver={(e) => handleDragOver(e, zone)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, zone)}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            {t('Standby')}
          </span>
          {!isPlaying && !isExporting && (
            <Badge variant="secondary" className="text-[10px] h-4 px-1">
              {cards.length}
            </Badge>
          )}
        </div>
        <div className="flex flex-col gap-2 items-center overflow-y-auto" style={{ maxHeight: 'calc(100% - 2rem)' }}>
          {cards.length > 0 ? (
            cards.map((card, index) => (
              <div 
                key={card.instanceId}
                className={`transition-opacity duration-500 ${
                  card.fadeState === 'entering' ? 'opacity-0 animate-pulse' : 
                  card.fadeState === 'leaving' ? 'opacity-50' : 'opacity-100'
                }`}
              >
                <CardThumbnail card={card} zone={zone} index={index} />
              </div>
            ))
          ) : (
            <div className="text-[10px] text-muted-foreground/50 flex items-center text-center px-1 py-4">
              {t('Drag here')}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Flying card animation component - renders within overlay using absolute positioning
  const FlyingCard = ({ card, fromPos, toPos, cardWidth = 48, cardHeight = 79, onComplete }) => {
    const ref = useRef(null);
    const imageUrl = getCardImageUrl(card, 'thumbnail');
    const isScene = card.feature === 'scene' || card.feature?.value === 'scene';
    const w = isScene ? 80 : cardWidth;
    const h = isScene ? 67 : cardHeight;
    
    useEffect(() => {
      if (!ref.current) return;
      
      const dx = toPos.x - fromPos.x;
      const dy = toPos.y - fromPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Subtle lift — small arc proportional to distance, feels like picking up & placing
      const arcHeight = Math.min(distance * 0.15 + 15, 60);
      
      // Midpoint of the arc
      const midX = (fromPos.x + toPos.x) / 2;
      const midY = Math.min(fromPos.y, toPos.y) - arcHeight;
      
      // Subtle scale bump to convey Z-lift
      const baseScale = 1;
      const peakScale = 1.1;
      
      const animation = ref.current.animate([
        { 
          left: `${fromPos.x}px`, 
          top: `${fromPos.y}px`, 
          transform: `translate(-50%, -50%) scale(${baseScale})`,
          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
          opacity: 1,
        },
        { 
          left: `${midX}px`, 
          top: `${midY}px`, 
          transform: `translate(-50%, -50%) scale(${peakScale})`,
          filter: 'drop-shadow(0 16px 32px rgba(0,0,0,0.5))',
          opacity: 1,
          offset: 0.5,
        },
        { 
          left: `${toPos.x}px`, 
          top: `${toPos.y}px`, 
          transform: `translate(-50%, -50%) scale(${baseScale})`,
          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
          opacity: 1,
        },
      ], {
        duration: 450,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'forwards',
      });
      
      animation.onfinish = onComplete;
      
      return () => animation.cancel();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    
    return (
      <div 
        ref={ref}
        className="absolute pointer-events-none"
        style={{ 
          left: fromPos.x, 
          top: fromPos.y, 
          transform: 'translate(-50%, -50%)',
          zIndex: 100,
        }}
      >
        <div 
          className="rounded border-2 border-white/60 overflow-hidden bg-slate-700 shadow-2xl"
          style={{ width: w, height: h }}
        >
          {imageUrl ? (
            <img
              src={getProxyImageUrl(imageUrl)}
              alt={card.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <Image className="h-5 w-5 text-slate-400 m-auto" />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* CSS for fade animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 0.7; transform: translateX(0); }
        }
        @keyframes fadeOut {
          from { opacity: 0.7; transform: translateX(0); }
          to { opacity: 0; transform: translateX(-20px); }
        }
        .animate-fade-in { animation: fadeIn 0.5s ease-out forwards; }
        .animate-fade-out { animation: fadeOut 0.5s ease-out forwards; }
      `}</style>
      
      {/* Header */}
      <div className="p-4 border-b bg-card flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('navigation.theaterMode') || 'Theater Mode (Alpha)'}</h1>
            <p className="text-sm text-muted-foreground">
              {t('Create animated GIFs showing card game states - drag cards between zones')}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowSavedTheaters(true)}>
              {savedTheaters.length > 0 ? (
                <FolderOpen className="h-4 w-4 mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {t('savedTheaters.title')}
            </Button>
            <Button variant="outline" onClick={exportGif} disabled={isExporting}>
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {isExporting ? t('Exporting...') : t('Export GIF')}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Off-Screen Zone + Effects - Left side */}
        <div className="w-64 border-l bg-card flex flex-col flex-shrink-0">
          <OffScreenZone 
            cards={currentSlide?.zones[ZONES.OFF_SCREEN] || []} 
          />
          {/* Effects Panel */}
          {!isPlaying && (
            <div className="border-t border-border p-3 flex-shrink-0">
              <div className="flex items-center gap-1 mb-2">
                <Sparkles className="h-3 w-3 text-yellow-400" />
                <span className="text-[10px] font-medium text-black uppercase tracking-wider">
                  Effects
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                {Object.entries(CARD_EFFECTS).map(([key, effect]) => (
                  <div
                    key={key}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border bg-muted/50 cursor-grab active:cursor-grabbing hover:bg-muted transition-colors"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'copy';
                      e.dataTransfer.setData('application/effect', key);
                      setDraggingEffect(key);
                    }}
                    onDragEnd={() => setDraggingEffect(null)}
                  >
                    {effect.isRemove ? (
                      <X className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: effect.color,
                          boxShadow: `0 0 8px 2px ${effect.color}80`,
                        }}
                      />
                    )}
                    <span className="text-[11px] text-black">{effect.label}</span>
                  </div>
                ))}
              </div>
              <p className="text-[9px] text-black/50 mt-2">
                Drag onto a card to apply
              </p>
            </div>
          )}
        </div>
        
        {/* Main Board Area */}
        <div className="flex-1 flex flex-col p-4 overflow-auto">
          {/* Game Board Layout - Vertical stacking with animation overlay */}
          <Card 
            ref={gameBoardRef}
            className="flex-1 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 border-slate-700 relative"
          >
            {/* Flying card animation overlay */}
            {flyingCards.length > 0 && (
              <div className="absolute inset-0 z-50 pointer-events-none overflow-hidden">
                {flyingCards.map(fc => (
                  <FlyingCard
                    key={fc.id}
                    card={fc.card}
                    fromPos={fc.fromPos}
                    toPos={fc.toPos}
                    cardWidth={fc.cardWidth}
                    cardHeight={fc.cardHeight}
                    onComplete={() => {
                      setFlyingCards(prev => prev.filter(f => f.id !== fc.id));
                      setAnimatingCardIds(prev => {
                        const next = new Set(prev);
                        next.delete(fc.card.instanceId);
                        return next;
                      });
                    }}
                  />
                ))}
              </div>
            )}
            
            {/* Caption overlay during playback / export */}
            {(isPlaying || isExporting) && currentSlide?.caption && (
              <div className="absolute top-0 left-0 right-0 z-40 pointer-events-none flex justify-center">
                <div className="bg-black/70 text-white px-8 py-4 rounded-b-xl text-2xl font-bold max-w-[90%] text-center leading-snug">
                  {currentSlide.caption}
                </div>
              </div>
            )}
            
            <CardContent className="p-4 h-full flex flex-col gap-2">
              
              {/* Opponent Hand Row: Discard - Hand - Deck */}
              <div className="flex gap-2 items-stretch flex-shrink-0">
                <div className="w-24 flex-shrink-0">
                  <DeckZone 
                    zone={ZONES.OPPONENT_DISCARD} 
                    cards={currentSlide?.zones[ZONES.OPPONENT_DISCARD] || []} 
                    className="h-full"
                  />
                </div>
                <div className="flex-1">
                  <ExpandedZone
                    zone={ZONES.OPPONENT_HAND}
                    cards={currentSlide?.zones[ZONES.OPPONENT_HAND] || []}
                    minHeight="50px"
                  />
                </div>
                <div className="w-24 flex-shrink-0">
                  <DeckZone 
                    zone={ZONES.OPPONENT_DECK} 
                    cards={currentSlide?.zones[ZONES.OPPONENT_DECK] || []} 
                    className="h-full"
                  />
                </div>
              </div>

              {/* Opponent Field - expands equally */}
              <FieldZone
                zone={ZONES.OPPONENT_FIELD}
                cards={currentSlide?.zones[ZONES.OPPONENT_FIELD] || []}
                className="flex-1"
              />
              
              {/* Scene Area - Smaller centered zone */}
              <div className="flex-shrink-0 flex justify-center">
                <SceneZone
                  cards={currentSlide?.zones[ZONES.SCENE] || []}
                />
              </div>
              
              {/* Player Field - expands equally */}
              <FieldZone
                zone={ZONES.PLAYER_FIELD}
                cards={currentSlide?.zones[ZONES.PLAYER_FIELD] || []}
                className="flex-1"
              />

              {/* Player Hand Row: Discard - Hand - Deck */}
              <div className="flex gap-2 items-stretch flex-shrink-0">
                <div className="w-24 flex-shrink-0">
                  <DeckZone 
                    zone={ZONES.PLAYER_DISCARD} 
                    cards={currentSlide?.zones[ZONES.PLAYER_DISCARD] || []} 
                    className="h-full"
                  />
                </div>
                <div className="flex-1">
                  <ExpandedZone
                    zone={ZONES.PLAYER_HAND}
                    cards={currentSlide?.zones[ZONES.PLAYER_HAND] || []}
                    minHeight="60px"
                  />
                </div>
                <div className="w-24 flex-shrink-0">
                  <DeckZone 
                    zone={ZONES.PLAYER_DECK} 
                    cards={currentSlide?.zones[ZONES.PLAYER_DECK] || []} 
                    className="h-full"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Caption area */}
          <div className="mt-3 p-3 border rounded-lg bg-card">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">{t('Caption')}:</span>
              {editingCaption ? (
                <div className="flex-1 ml-2 flex gap-2">
                  <Input
                    value={captionText}
                    onChange={(e) => setCaptionText(e.target.value)}
                    placeholder={t('Enter slide caption...')}
                    className="flex-1"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && saveCaption()}
                  />
                  <Button size="icon" variant="ghost" onClick={saveCaption}>
                    <Check className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex-1 ml-2 flex items-center gap-2">
                  <span className="text-foreground">
                    {currentSlide?.caption || t('No caption')}
                  </span>
                  <Button size="icon" variant="ghost" onClick={startEditCaption}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Slide Panel */}
        <div className="w-64 border-l bg-card flex flex-col flex-shrink-0">
          <div className="p-3 border-b">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">{t('Slides')}</h3>
              <Button size="icon" variant="ghost" onClick={addSlide} title={t('Add slide')}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={togglePlayback}
                className="flex-1"
              >
                {isPlaying ? (
                  <>
                    <Pause className="h-4 w-4 mr-1" /> {t('Pause')}
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-1" /> {t('Play')}
                  </>
                )}
              </Button>
            </div>
          </div>

          <ScrollArea className="flex-1">
            <div className="p-2 space-y-2">
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`p-2 rounded border cursor-pointer transition-colors overflow-hidden ${
                    index === currentSlideIndex
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:bg-muted'
                  }`}
                  onClick={() => goToSlide(index)}
                >
                  <div className="flex items-center justify-between min-w-0">
                    <span className="text-sm font-medium truncate">{t('Slide')} {index + 1}</span>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentSlideIndex(index);
                          duplicateSlide();
                        }}
                        title={t('Duplicate')}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                      {slides.length > 1 && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentSlideIndex(index);
                            setTimeout(deleteSlide, 0);
                          }}
                          title={t('Delete')}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-1">
                    {slide.caption || t('No caption')}
                  </div>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {Object.entries(slide.zones || {}).map(([zone, cards]) => (
                      cards && cards.length > 0 && (
                        <Badge key={zone} variant="secondary" className="text-[9px] px-1 py-0">
                          {ZONE_CONFIG[zone]?.label?.slice(0, 3) || zone.slice(0, 3)}: {cards.length}
                        </Badge>
                      )
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Navigation */}
          <div className="p-3 border-t">
            <div className="flex items-center justify-between">
              <Button
                size="icon"
                variant="outline"
                onClick={prevSlide}
                disabled={currentSlideIndex === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                {currentSlideIndex + 1} / {slides.length}
              </span>
              <Button
                size="icon"
                variant="outline"
                onClick={nextSlide}
                disabled={currentSlideIndex === slides.length - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Card Search Dialog */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              {t('Add Card to')} {ZONE_CONFIG[targetZone]?.label || targetZone}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('Search for a card by name...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>
            
            <ScrollArea className="h-[400px] border rounded-lg">
              <div className="p-3">
                {searchLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="grid grid-cols-5 gap-3">
                    {/* Placeholder card-back as first option */}
                    <div
                      className="border rounded-lg p-2 cursor-pointer hover:bg-accent transition-colors group"
                      onClick={() => addCardToZone({
                        name: 'Card Back',
                        number: 'Placeholder',
                        feature: null,
                        images: { blob: { thumbnail: CARD_BACK_IMAGE, full: CARD_BACK_IMAGE } },
                      }, targetZone)}
                    >
                      <div className="rounded overflow-hidden bg-muted mb-2 aspect-[220/305]">
                        <img
                          src={CARD_BACK_IMAGE}
                          alt="Card Back"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="text-xs font-medium truncate group-hover:text-primary">
                        Card Back
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        Placeholder
                      </div>
                    </div>
                    {searchResults.map((card, index) => {
                      const imageUrl = getCardImageUrl(card, 'thumbnail');
                      const isScene = card.feature?.value === 'scene' || card.cardType === 'Scene';
                      
                      return (
                        <div
                          key={card.id || index}
                          className="border rounded-lg p-2 cursor-pointer hover:bg-accent transition-colors group"
                          onClick={() => addCardToZone(card, targetZone)}
                        >
                          <div className={`rounded overflow-hidden bg-muted mb-2 ${isScene ? 'aspect-[305/220]' : 'aspect-[220/305]'}`}>
                            {imageUrl ? (
                              <img
                                src={getProxyImageUrl(imageUrl)}
                                alt={card.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Image className="h-8 w-8 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div className="text-xs font-medium truncate group-hover:text-primary">
                            {card.name}
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            {card.number}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : searchQuery ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Image className="h-12 w-12 mb-2 opacity-50" />
                    <p>{t('No cards found for')} "{searchQuery}"</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Search className="h-12 w-12 mb-2 opacity-50" />
                    <p>{t('Type to search for cards')}</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('Delete Card')}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground">
              {t('Are you sure you want to permanently delete')} <strong>{deleteConfirm?.cardName}</strong>?
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              {t('Cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                if (deleteConfirm) {
                  deleteCardFromZone(deleteConfirm.zone, deleteConfirm.instanceId);
                  setDeleteConfirm(null);
                }
              }}
            >
              {t('Delete')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Saved Theaters Dialog */}
      <SavedTheatersDialog
        open={showSavedTheaters}
        onOpenChange={setShowSavedTheaters}
        savedTheaters={savedTheaters}
        onLoad={(theater) => {
          if (theater.slides && theater.slides.length > 0) {
            setSlides(theater.slides);
            setCurrentSlideIndex(0);
          }
        }}
        onSave={(name) => {
          saveTheater({ name, slides });
        }}
        onDelete={deleteTheater}
        onRename={renameTheater}
        canSaveMore={canSaveMore}
        maxTheaters={maxTheaters}
        currentEmpty={slides.length === 1 && Object.values(slides[0].zones).every(z => !z || z.length === 0)}
      />
    </div>
  );
}
